import React, { useEffect } from 'react';
import { CheckCircle, ArrowRight } from 'lucide-react';

const SignOutConfirmation = () => {
  useEffect(() => {
    // Make sign-out idempotent: if previous page redirected too quickly,
    // retry extension/session cleanup from this confirmation page.
    const clearWebStorage = () => {
      try {
        localStorage.removeItem('trafficLiteToken');
        localStorage.removeItem('trafficLiteUser');
        localStorage.removeItem('trafficLiteRole');
        localStorage.removeItem('traffic-lite-selected-classroom');
        localStorage.removeItem('traffic-lite-session-data');
        localStorage.removeItem('traffic-lite-teacher-question');
      } catch (error) {
        console.warn('SignOutConfirmation: localStorage cleanup warning:', error);
      }
    };

    const postBridgeClearMessage = () => {
      window.postMessage(
        {
          source: 'trafficlite-web',
          action: 'CLEAR_EXTENSION_AUTH'
        },
        window.location.origin
      );
    };

    clearWebStorage();
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 8;

    const onBridgeResult = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data || {};
      if (data.source !== 'trafficlite-extension' || data.action !== 'CLEAR_EXTENSION_AUTH_RESULT') return;
      if (data.success) {
        cancelled = true;
      }
    };

    window.addEventListener('message', onBridgeResult);

    const intervalId = window.setInterval(() => {
      if (cancelled || attempts >= maxAttempts) {
        window.clearInterval(intervalId);
        return;
      }
      attempts += 1;
      postBridgeClearMessage();
    }, 250);

    postBridgeClearMessage();

    return () => {
      cancelled = true;
      window.removeEventListener('message', onBridgeResult);
      window.clearInterval(intervalId);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-100 p-8 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
        {/* Success Message */}
        <div className="text-center mb-8">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Successfully Signed Out!
          </h1>
          <p className="text-lg text-gray-700">
            You have been signed out of Traffic Lite.
          </p>
        </div>

        {/* Instructions */}
        <div className="space-y-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-blue-900 text-base">
              <span className="font-semibold">To Sign Back In</span>, click the Traffic Lite Extension icon in your Chrome toolbar.
            </p>
          </div>

          {/* Note */}
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600">
              <strong>Note:</strong> You can now close this tab. The Traffic Lite extension will start fresh when you click it again.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignOutConfirmation;
